/**
 * 日付操作のユーティリティ関数
 * Luxonを使用して日時操作と変換を行う
 */
import { DateTime } from 'luxon';

// 定数
const JST_TIMEZONE = 'Asia/Tokyo';
const ISO_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"; // ISO8601フォーマット (UTC)
const FORM_FORMAT = "yyyy-MM-dd'T'HH:mm"; // フォームで使用するフォーマット
const DATE_FORMAT = "yyyy-MM-dd"; // 日付のみのフォーマット
const TIME_FORMAT = "HH:mm"; // 時間のみのフォーマット
const DISPLAY_FORMAT = "yyyy年MM月dd日 HH:mm"; // 表示用フォーマット

/**
 * 週の始めの日を取得する (日曜日始まり)
 * @param date 基準となる日付
 * @returns 週の始めの日のDateオブジェクト
 */
export const getStartOfWeek = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0が日曜日、1が月曜日...6が土曜日
  d.setDate(d.getDate() - day); // 今週の日曜日に調整
  d.setHours(0, 0, 0, 0); // 時刻を00:00:00.000に設定
  return d;
};

/**
 * 時間をフォーマットする (HH:mm)
 * @param dateString ISO8601形式の日時文字列
 * @returns フォーマットされた時間文字列
 */
export const formatTime = (dateString: string) => {
  if (!dateString) return '';
  
  try {
    return DateTime.fromISO(dateString)
      .setZone(JST_TIMEZONE)
      .toFormat(TIME_FORMAT);
  } catch (error) {
    console.error('時間フォーマットエラー:', error);
    return '--:--';
  }
};

/**
 * 日付をフォーマットする (MM/DD(曜日))
 * @param dateString ISO8601形式の日時文字列
 * @returns フォーマットされた日付文字列
 */
export const formatDate = (dateString: string) => {
  if (!dateString) return '';
  
  try {
    return DateTime.fromISO(dateString)
      .setZone(JST_TIMEZONE)
      .setLocale('ja')
      .toFormat('MM/dd(ccc)');
  } catch (error) {
    console.error('日付フォーマットエラー:', error);
    return '';
  }
};

/**
 * 日付部分のみを置き換え、時間は維持する
 * @param dateTimeStr 元の日時文字列
 * @param newDate 新しい日付文字列 (YYYY-MM-DD)
 * @returns 新しい日時のISO文字列
 */
export const updateDateKeepTime = (dateTimeStr: string, newDate: string) => {
  try {
    // 元の日時をパース
    const original = DateTime.fromISO(dateTimeStr);
    if (!original.isValid) {
      throw new Error(`Invalid original date: ${dateTimeStr}`);
    }
    
    // 新しい日付をパース
    const newDateObj = DateTime.fromISO(newDate);
    if (!newDateObj.isValid) {
      throw new Error(`Invalid new date: ${newDate}`);
    }
    
    // 年月日を置き換え、時間は維持
    const result = original.set({
      year: newDateObj.year,
      month: newDateObj.month,
      day: newDateObj.day
    });
    
    return result.toUTC().toISO();
  } catch (error) {
    console.error('日時変換エラー:', error);
    // エラーの場合はデフォルト値を返す
    return DateTime.fromISO(`${newDate}T00:00:00.000Z`).toUTC().toISO() || '';
  }
};

/**
 * 日付を指定形式に変換し、タイムゾーンの影響を受けないようにする
 * @param date 処理したい日付
 * @returns YYYY-MM-DD形式の文字列
 */
export const formatDateToLocalISOString = (date: Date): string => {
  return DateTime.fromJSDate(date).toFormat(DATE_FORMAT);
};

/**
 * 選択した日付からタイムゾーンを考慮して正確な日付文字列を取得する
 * カレンダーで選択された日付を予定作成モーダルに渡す際に使用
 * @param selectedDate 選択された日付
 * @returns YYYY-MM-DD形式の文字列
 */
export const getLocalDateString = (selectedDate: Date): string => {
  return formatDateToLocalISOString(selectedDate);
};

/**
 * フォームで入力されたローカル日時（JST）をUTC日時に変換する
 * この関数はフォームから送信するデータの前処理に使用する
 * @param localDateTimeString フォームで入力された日時文字列 (YYYY-MM-DDTHH:MM形式)
 * @returns UTC日時のISO文字列
 */
export const convertToUTCString = (localDateTimeString: string): string => {
  if (!localDateTimeString) return '';
  
  try {
    // フォームの日時文字列をJSTとして解釈し、ISOに変換
    const jst = DateTime.fromFormat(localDateTimeString, FORM_FORMAT, { zone: JST_TIMEZONE });
    
    if (!jst.isValid) {
      throw new Error(`Invalid datetime format: ${localDateTimeString}`);
    }
    
    // JSTとUTCの情報をログ出力（デバッグ用）
    const utc = jst.toUTC();
    
    console.log(`日時変換: JST ${jst.toFormat(DISPLAY_FORMAT)} → UTC ${utc.toFormat(DISPLAY_FORMAT)}`);
    console.log(`ISO形式: ${utc.toISO()}`);
    
    return utc.toISO() || '';
  } catch (error) {
    console.error('UTC変換エラー:', error);
    return '';
  }
};

/**
 * UTCの日時をローカルタイム(JST)のフォーム入力用文字列に変換
 * @param utcDateString UTC日時のISO文字列
 * @returns YYYY-MM-DDTHH:MM形式のローカル日時文字列
 */
export const convertUTCToLocalFormString = (utcDateString: string): string => {
  if (!utcDateString) return '';
  
  try {
    // UTC日時をパース
    const utc = DateTime.fromISO(utcDateString);
    
    if (!utc.isValid) {
      throw new Error(`Invalid ISO datetime: ${utcDateString}`);
    }
    
    // UTCからJSTに変換
    const jst = utc.setZone(JST_TIMEZONE);
    
    console.log(`UTC→JST変換: UTC ${utc.toFormat(DISPLAY_FORMAT)} → JST ${jst.toFormat(DISPLAY_FORMAT)}`);
    
    // フォーム用のフォーマットに変換
    return jst.toFormat(FORM_FORMAT);
  } catch (error) {
    console.error('ローカル日時フォーマットエラー:', error);
    return '';
  }
};

/**
 * 日時をブラウザ表示用にフォーマットする
 * @param dateString ISO8601形式の日時文字列
 * @returns 表示用にフォーマットされた文字列
 */
export const formatDateTimeForDisplay = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    return DateTime.fromISO(dateString)
      .setZone(JST_TIMEZONE)
      .toFormat(DISPLAY_FORMAT);
  } catch (error) {
    console.error('表示用日時フォーマットエラー:', error);
    return '';
  }
};

/**
 * 2つの日時が同じ日かどうかをチェックする
 * @param date1 比較する日時1
 * @param date2 比較する日時2
 * @returns 同じ日ならtrue、そうでなければfalse
 */
export const isSameDay = (date1: string | Date, date2: string | Date): boolean => {
  const dt1 = typeof date1 === 'string' ? DateTime.fromISO(date1) : DateTime.fromJSDate(date1);
  const dt2 = typeof date2 === 'string' ? DateTime.fromISO(date2) : DateTime.fromJSDate(date2);
  
  return dt1.hasSame(dt2, 'day');
};

/**
 * Dateオブジェクトが有効かどうかを確認
 * @param date チェックする日付
 * @returns 有効な日付ならtrue、そうでなければfalse
 */
export const isValidDate = (date: Date): boolean => {
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * 日付文字列をフォーマットして表示用にする
 * @param dateString 日付文字列
 * @param format フォーマット (デフォルト: yyyy年MM月dd日)
 * @returns フォーマット済みの日付文字列
 */
export const formatDateString = (dateString: string, format = 'yyyy年MM月dd日'): string => {
  if (!dateString) return '';
  
  try {
    return DateTime.fromISO(dateString)
      .setZone(JST_TIMEZONE)
      .setLocale('ja')
      .toFormat(format);
  } catch (error) {
    console.error('日付文字列フォーマットエラー:', error);
    return '';
  }
};

/**
 * 現在の日付をYYYY-MM-DD形式で取得
 * @returns YYYY-MM-DD形式の現在日付
 */
export const getCurrentDateString = (): string => {
  return DateTime.now().toFormat(DATE_FORMAT);
};